import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Heart, Shield, Clock, Users, ArrowRight, CheckCircle, Brain } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import AuthPage from '../auth/AuthPage'

export const LandingPage: React.FC = () => {
  // 显示完整的营销页面，带有优化的认证入口
  return <OriginalLandingPage />
}

// 认证后的管理界面组件（备用）
const AuthenticatedDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { carer } = useAuth()

  return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        {/* 认证后的用户界面 */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">必</span>
                </div>
                <h1 className="text-2xl font-bold text-gray-900">必顾可伴</h1>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-gray-700">欢迎，{carer?.name}</span>
                <button
                  onClick={() => navigate('/users')}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  管理用户
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* 主功能区 */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              ICOPE老年综合照护系统
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              基于WHO标准的专业评估，为老年人提供个性化的健康指导和照护建议
            </p>
          </div>

          {/* 快速操作 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div 
              onClick={() => navigate('/users')}
              className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer border-2 hover:border-blue-200"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">用户管理</h3>
              <p className="text-gray-600 text-center text-sm">查看和管理用户档案</p>
            </div>

            <div 
              onClick={() => navigate('/assessment-choice')}
              className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer border-2 hover:border-green-200"
            >
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Heart className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">开始评估</h3>
              <p className="text-gray-600 text-center text-sm">选择评估类型进行评估</p>
            </div>
            <div 
              onClick={() => navigate('/risk-assessment')}
              className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer border-2 hover:border-red-200"
            >
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">风险评估</h3>
              <p className="text-gray-600 text-center text-sm">15项核心风险深度分析</p>
            </div>

            <div 
              onClick={() => navigate('/functional-capability-test')}
              className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer border-2 hover:border-orange-200"
            >
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Brain className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">功能能力评估</h3>
              <p className="text-gray-600 text-center text-sm">基于可靠性分级40项功能分析</p>
            </div>

            <div 
              onClick={() => navigate('/admin')}
              className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer border-2 hover:border-purple-200"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">系统管理</h3>
              <p className="text-gray-600 text-center text-sm">数据管理和系统设置</p>
            </div>
          </div>
        </main>
      </div>
    )
}

// 以下是原来的LandingPage内容（作为备用）
const OriginalLandingPage: React.FC = () => {
  const navigate = useNavigate()

  const benefits = [
    {
      icon: <Clock className="w-6 h-6 text-primary-500" />,
      title: "10分钟快速评估",
      description: "基于WHO标准的科学评估方法"
    },
    {
      icon: <Shield className="w-6 h-6 text-primary-500" />,
      title: "权威专业指导",
      description: "来自国际权威健康组织的建议"
    },
    {
      icon: <Heart className="w-6 h-6 text-primary-500" />,
      title: "个性化关怀方案",
      description: "根据评估结果定制专属建议"
    },
    {
      icon: <Users className="w-6 h-6 text-primary-500" />,
      title: "专业顾问支持",
      description: "必要时连接专业健康顾问"
    }
  ]

  const assessmentDomains = [
    { name: "认知能力", description: "记忆力、判断力评估" },
    { name: "活动能力", description: "日常活动和体能测试" },
    { name: "营养状况", description: "饮食和营养健康检查" },
    { name: "视力状况", description: "视觉功能评估" },
    { name: "听力状况", description: "听觉能力检测" },
    { name: "心理状态", description: "情绪和心理能力评估" }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">必</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">必顾可伴</h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#about" className="text-gray-600 hover:text-primary-600 transition-colors">关于我们</a>
              <a href="#features" className="text-gray-600 hover:text-primary-600 transition-colors">功能特色</a>
              <a href="#contact" className="text-gray-600 hover:text-primary-600 transition-colors">联系我们</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            关怀从了解开始
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            基于世界卫生组织ICOPE标准，为您和家人提供专业的内在能力评估，
            让健康管理更科学、更贴心
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => navigate('/auth?mode=register')}
              className="btn-primary flex items-center space-x-2 text-lg px-8 py-4"
            >
              <span>开始免费评估</span>
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate('/auth?mode=login')}
              className="btn-secondary flex items-center space-x-2 text-lg px-8 py-4"
            >
              <Users className="w-5 h-5" />
              <span>用户档案</span>
            </button>
          </div>

          <div className="mt-8 flex items-center justify-center space-x-6 text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>完全免费</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>隐私保护</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>科学权威</span>
            </div>
          </div>

        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">为什么选择必顾可伴？</h3>
            <p className="text-lg text-gray-600">专业、便捷、贴心的健康评估体验</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="card text-center">
                <div className="flex justify-center mb-4">
                  {benefit.icon}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h4>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Assessment Overview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">六项内在能力评估</h3>
            <p className="text-lg text-gray-600">全面了解身体和心理的健康状况</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assessmentDomains.map((domain, index) => (
              <div key={index} className="card">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-primary-600 font-semibold">{index + 1}</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">{domain.name}</h4>
                </div>
                <p className="text-gray-600">{domain.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">
            现在开始，关爱自己和家人
          </h3>
          <p className="text-xl text-primary-100 mb-8">
            只需10分钟，获得专业的健康评估报告
          </p>
          <button
            onClick={() => navigate('/auth?mode=register')}
            className="bg-white text-primary-600 hover:bg-gray-50 font-semibold py-4 px-8 rounded-lg text-lg transition-colors flex items-center space-x-2 mx-auto"
          >
            <span>立即开始评估</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">必</span>
                </div>
                <h4 className="text-lg font-semibold">必顾可伴</h4>
              </div>
              <p className="text-gray-300">
                基于WHO ICOPE标准的专业内在能力评估平台，为老年人及其家庭提供科学的健康管理服务。
              </p>
            </div>
            <div>
              <h5 className="text-lg font-semibold mb-4">服务内容</h5>
              <ul className="space-y-2 text-gray-300">
                <li>内在能力评估</li>
                <li>个性化健康建议</li>
                <li>专业顾问咨询</li>
                <li>健康监测跟踪</li>
              </ul>
            </div>
            <div>
              <h5 className="text-lg font-semibold mb-4">联系我们</h5>
              <ul className="space-y-2 text-gray-300">
                <li>客服邮箱：support@bigukeban.com</li>
                <li>技术支持：tech@bigukeban.com</li>
                <li>服务时间：周一至周五 9:00-18:00</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 必顾可伴. 保留所有权利.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}